import { Box, Skeleton, Stack } from "@mui/material";
import { useQrCodeDataUrl } from "@src/appV2/lib/utils/useQrCodeDataUrl";
import { Image, Text } from "@src/appV2/redesign/system/ui-components";
import {
  touchpointCodeGenerator,
  type TouchpointCodeGeneratorProps,
} from "@src/appV2/Shifts/Shift/TouchpointIntegration/touchpointCodeGenerator";
import { useEffect } from "react";

interface TouchpointQrCodeProps {
  qrCodeProps: TouchpointCodeGeneratorProps;
  enabled?: boolean;
  onQrCodeReady?: () => void;
}

export function TouchpointQrCode(props: TouchpointQrCodeProps) {
  const { qrCodeProps, enabled = true, onQrCodeReady } = props;

  const qrCodeData = touchpointCodeGenerator(qrCodeProps);

  const {
    data: touchpointQrDataImageLink,
    isLoading: isLoadingTouchpointQrDataImage,
    isSuccess: isTouchpointQrDataImageSuccess,
    isError: isTouchpointQrError,
  } = useQrCodeDataUrl(
    {
      data: qrCodeData,
      qrOptions: { margin: 1, scale: 10 },
    },
    {
      enabled,
      meta: {
        userErrorMessage: "Failed to generate TouchPoint integration QR code",
      },
    }
  );

  useEffect(() => {
    if (enabled && isTouchpointQrDataImageSuccess) {
      onQrCodeReady?.();
    }
  }, [enabled, isTouchpointQrDataImageSuccess, onQrCodeReady]);

  return (
    <Stack spacing={2} alignItems="center">
      <Box
        sx={{
          width: "100%",
          maxWidth: "70vw",
          maxHeight: "50vh",
          aspectRatio: "1/1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoadingTouchpointQrDataImage && (
          <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
        )}
        {isTouchpointQrDataImageSuccess && (
          <Image
            src={touchpointQrDataImageLink}
            alt="TouchPoint QR Code"
            width="100%"
            height="100%"
            sx={{
              objectFit: "contain",
            }}
          />
        )}
        {isTouchpointQrError && (
          <Stack spacing={4} alignItems="center">
            <Text semibold variant="h5" color={(theme) => theme.palette.error.main}>
              QR Code Error
            </Text>
            <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
              Something went wrong while loading the QR code. Please try again.
            </Text>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
